import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, BookOpen, Store } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { catNoOf } from "../lib/localize";
import { ShareButtons } from "../components/ShareButtons";
import { SEO } from "../components/SEO";

// Seuls les fournisseurs de catalogues connus peuvent être intégrés
const ALLOWED_HOSTS = [
  "awardsofdistinction.ca", "fivestarrecognition.ca", "tromartawards.com",
  "online.fliphtml5.com", "caldwellrecognition.com", "s3.amazonaws.com",
  "urbanointernational.com", "cld.bz", "awardsandrecognition.ca",
];

// Fournisseurs qui bloquent l'intégration iframe (X-Frame-Options: SAMEORIGIN,
// vérifié le 2026-09-05) : on montre une page d'ouverture élégante à la place.
const BLOCKED_HOSTS = ["caldwellrecognition.com", "awardsandrecognition.ca"];

export default function CatalogueViewer() {
  const [params] = useSearchParams();
  const { t, lang } = useLang();
  const src = params.get("src") || "";
  const nom = params.get("nom") || "Catalogue";
  const [cover, setCover] = useState("");

  let host = "";
  try {
    host = new URL(src).hostname.replace(/^www\./, "");
  } catch { /* URL invalide */ }
  const blocked = BLOCKED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  const allowed = src.startsWith("http") && ALLOWED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  // Les liens http:// sont bloqués (contenu mixte) : on force https:// pour l'iframe
  const safeSrc = src.replace(/^http:\/\//i, "https://");

  // Page de repli (fournisseur anti-iframe) : grande couverture du catalogue
  useEffect(() => {
    if (!blocked) return;
    const catNo = catNoOf(nom);
    if (!catNo) return;
    api.get("/catalogue-covers")
      .then((r) => setCover(r.data?.covers?.[catNo] || ""))
      .catch(() => {});
  }, [blocked, nom]);

  return (
    <div data-testid="catalogue-viewer" className="pt-28 pb-10 min-h-screen flex flex-col">
      {/* Page utilitaire (visionneuse iframe) : contenu non lisible par les robots
          et URL partagée par tous les catalogues → noindex ; la valeur SEO vit sur
          /catalogues (page riche, indexable) */}
      <SEO title={`${nom} — Catalogue | Mr Trophée`}
        description={lang === "fr"
          ? `Feuilletez le catalogue ${nom} en ligne : trophées, médailles, plaques et cadeaux d'entreprise gravés par Mr Trophée, atelier de Léry (Montérégie) — livraison partout au Canada.`
          : `Browse the ${nom} catalogue online: trophies, medals, plaques and engraved corporate gifts by Mr Trophée, Léry workshop (Monteregie) — shipping across Canada.`}
        path="/catalogue-viewer" noindex />
      {/* Pleine largeur d'écran : la visionneuse occupe toute la page */}
      <div className="w-full px-2 sm:px-4 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 max-w-7xl mx-auto w-full">
          <div>
            <Link to="/catalogues" data-testid="viewer-back"
              className="inline-flex items-center gap-2 text-ivory/70 hover:text-gold text-sm mb-2 transition-colors duration-300">
              <ArrowLeft className="w-4 h-4" /> {t("catalogues.viewer_back")}
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl" data-testid="viewer-title">{nom}</h1>
          </div>
          {src.startsWith("http") && (
            <a
              href={src} target="_blank" rel="noreferrer" data-testid="viewer-external"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <ExternalLink className="w-4 h-4" /> {t("catalogues.viewer_open")}
            </a>
          )}
        </div>

        {blocked ? (
          /* Fournisseur anti-iframe : page conteneur pleine largeur élégante */
          <div className="flex-1 w-full max-w-7xl mx-auto px-2 sm:px-4" data-testid="viewer-fallback">
            <div className="grid md:grid-cols-[minmax(0,360px)_1fr] gap-10 lg:gap-16 items-center border border-gold/20 rounded-2xl bg-surface p-8 md:p-12 spotlight">
              {cover ? (
                <img src={cover} alt={nom} data-testid="viewer-fallback-cover"
                  className="w-full rounded-xl border border-ivory/15 shadow-[0_20px_60px_rgba(0,0,0,0.5)]" />
              ) : (
                <div className="w-full aspect-[3/4] rounded-xl border border-ivory/15 bg-ink flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-gold/40" />
                </div>
              )}
              <div>
                <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("catalogues.viewer_blocked_kicker")}</p>
                <h2 className="font-serif text-3xl md:text-4xl mb-5">{nom}</h2>
                <p className="text-ivory/60 text-sm leading-relaxed mb-9 max-w-lg">{t("catalogues.viewer_blocked_text")}</p>
                <div className="flex flex-wrap gap-4 mb-10">
                  <a href={src} target="_blank" rel="noreferrer" data-testid="viewer-fallback-open"
                    className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300">
                    <ExternalLink className="w-4 h-4" /> {t("catalogues.viewer_open")}
                  </a>
                  <Link to="/boutique" data-testid="viewer-fallback-shop"
                    className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Store className="w-4 h-4" /> {t("catalogues.viewer_shop")}
                  </Link>
                </div>
                <ShareButtons title={nom} label={t("catalogues.viewer_share")} />
              </div>
            </div>
          </div>
        ) : allowed ? (
          <>
            <iframe
              src={safeSrc} title={nom} data-testid="viewer-frame"
              className="w-full flex-1 min-h-[82vh] rounded-xl border border-ivory/15 bg-white"
              allowFullScreen
            />
            <div className="max-w-7xl mx-auto w-full mt-5 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-ivory/65">{t("catalogues.viewer_help")}</p>
              <ShareButtons title={nom} label={t("catalogues.viewer_share")} />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-20" data-testid="viewer-invalid">
            <p className="text-ivory/60 mb-6">{t("catalogues.viewer_invalid")}</p>
            <Link to="/catalogues"
              className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300">
              {t("catalogues.viewer_back")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
